import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Safe area insets for Android nav bar / iOS notch.
 *
 * On native Android, @capacitor-community/safe-area injects real WindowInsets
 * into --safe-area-inset-* (WebView < 140 uses webview padding instead).
 * This hook reads those values and mirrors them to --navigation-bar-height.
 */

const MAX_NAV_BAR_PX = 80;
const KEYBOARD_GAP_PX = 120;

const emptyInsets = () => ({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  navigationBarHeight: 0,
});

const parsePx = (value) => {
  if (!value || value === 'auto') return 0;
  const parsed = parseFloat(String(value).replace('px', ''));
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
};

const readCssVar = (name) => {
  const styles = getComputedStyle(document.documentElement);
  return parsePx(styles.getPropertyValue(name));
};

const measureEnvInsets = () => {
  const measureElement = document.createElement('div');
  measureElement.style.cssText = `
    position: fixed;
    visibility: hidden;
    pointer-events: none;
    padding-top: env(safe-area-inset-top, 0px);
    padding-right: env(safe-area-inset-right, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    padding-left: env(safe-area-inset-left, 0px);
  `;
  document.body.appendChild(measureElement);
  const computedStyle = getComputedStyle(measureElement);
  const top = parsePx(computedStyle.paddingTop);
  const right = parsePx(computedStyle.paddingRight);
  const bottom = parsePx(computedStyle.paddingBottom);
  const left = parsePx(computedStyle.paddingLeft);
  document.body.removeChild(measureElement);
  return { top, right, bottom, left };
};

const getVisualViewportGap = () => {
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
};

const isKeyboardLikelyOpen = () => {
  const gap = getVisualViewportGap();
  if (gap >= KEYBOARD_GAP_PX) return true;
  const vv = window.visualViewport;
  if (!vv) return false;
  return vv.height < window.innerHeight * 0.75;
};

const applyCssVars = (insets) => {
  document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
  document.documentElement.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
  document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
  document.documentElement.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
  document.documentElement.style.setProperty('--navigation-bar-height', `${insets.navigationBarHeight}px`);
};

const computeInsets = (lastKnownNavBottom = 0) => {
  const injected = {
    top: readCssVar('--safe-area-inset-top'),
    right: readCssVar('--safe-area-inset-right'),
    bottom: readCssVar('--safe-area-inset-bottom'),
    left: readCssVar('--safe-area-inset-left'),
  };
  const measured = measureEnvInsets();

  let top = Math.max(injected.top, measured.top, 0);
  let right = Math.max(injected.right, measured.right, 0);
  let bottom = Math.max(injected.bottom, measured.bottom, 0);
  let left = Math.max(injected.left, measured.left, 0);

  const isNativeAndroid =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

  // Last-resort fallback when neither plugin nor env() report insets yet
  if (isNativeAndroid && bottom === 0 && !isKeyboardLikelyOpen()) {
    const gap = getVisualViewportGap();
    if (gap > 10 && gap <= MAX_NAV_BAR_PX) {
      bottom = gap;
    } else if (lastKnownNavBottom > 0 && lastKnownNavBottom <= MAX_NAV_BAR_PX) {
      bottom = lastKnownNavBottom;
    }
  }

  if (Capacitor.isNativePlatform() && isKeyboardLikelyOpen()) {
    bottom = Math.min(
      lastKnownNavBottom > 0 ? lastKnownNavBottom : Math.min(bottom, MAX_NAV_BAR_PX),
      MAX_NAV_BAR_PX
    );
  } else {
    bottom = Math.min(bottom, MAX_NAV_BAR_PX);
  }

  return {
    top,
    right,
    bottom,
    left,
    navigationBarHeight: bottom,
  };
};

export const useSafeAreaInsets = () => {
  const [insets, setInsets] = useState(emptyInsets);
  const lastNavBottomRef = useRef(0);
  const keyboardWasOpenRef = useRef(false);

  const updateInsets = useCallback(() => {
    try {
      const keyboardOpen = Capacitor.isNativePlatform() && isKeyboardLikelyOpen();
      const next = computeInsets(lastNavBottomRef.current);

      if (Capacitor.isNativePlatform() && keyboardWasOpenRef.current && !keyboardOpen) {
        keyboardWasOpenRef.current = false;
        const restored = computeInsets(lastNavBottomRef.current);
        lastNavBottomRef.current = restored.navigationBarHeight;
        setInsets(restored);
        applyCssVars(restored);
        requestAnimationFrame(() => {
          const maxScroll = Math.max(
            0,
            (document.documentElement.scrollHeight || document.body.scrollHeight) -
              window.innerHeight
          );
          if (window.scrollY > maxScroll) {
            window.scrollTo(0, maxScroll);
          }
        });
        return;
      }

      if (keyboardOpen) {
        keyboardWasOpenRef.current = true;
        return;
      }

      keyboardWasOpenRef.current = false;
      lastNavBottomRef.current = next.navigationBarHeight;
      setInsets(next);
      applyCssVars(next);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[useSafeAreaInsets] Error getting safe area insets:', error);
      }
      const fallback = emptyInsets();
      lastNavBottomRef.current = 0;
      setInsets(fallback);
      applyCssVars(fallback);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(updateInsets, 50);
    const delayedTimeout = setTimeout(updateInsets, 350);

    const handleResize = () => updateInsets();
    const handleOrientationChange = () => {
      setTimeout(updateInsets, 100);
      setTimeout(updateInsets, 400);
    };

    const handleFocusOut = () => {
      if (!Capacitor.isNativePlatform()) return;
      setTimeout(updateInsets, 180);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    if (Capacitor.isNativePlatform() && window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }
    if (Capacitor.isNativePlatform()) {
      document.addEventListener('focusout', handleFocusOut);
    }

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(delayedTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [updateInsets]);

  return insets;
};

export default useSafeAreaInsets;
